/**
 * Recorder Engine: high-quality audio recording
 * Primary path: AudioWorklet PCM -> WAV
 * Fallback: ScriptProcessor PCM -> WAV
 * Last resort: MediaRecorder -> decode -> WAV
 */

import WAVEncoder from "./WavEncoder";
import { PeaksComputer } from "./PeaksComputer";

export interface RecorderOptions {
  sampleRate?: number;
  channels?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  monitoringEnabled?: boolean;
  monitoringGain?: number;
  inputDeviceId?: string | null;
}

export interface DraftRecording {
  blob: Blob;
  format: "wav" | "webm_opus";
  sampleRate: number;
  channels: number;
  durationMs: number;
  peaksJson?: string;
}

export class RecorderEngine {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private analyserData: Uint8Array<ArrayBuffer> | null = null;
  private processorNode: AudioWorkletNode | null = null;
  private scriptProcessorNode: ScriptProcessorNode | null = null;
  private scriptProcessorSilence: GainNode | null = null;
  private pcmChunks: Float32Array[] = [];
  private isRecording = false;
  private recordingStartTime = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaRecorderChunks: Blob[] = [];
  private mediaRecorderMimeType: string | null = null;
  private monitorGainNode: GainNode | null = null;

  constructor(
    audioContext: AudioContext,
    private options: RecorderOptions = {}
  ) {
    this.audioContext = audioContext;
    this.options = {
      sampleRate: 48000,
      channels: 1,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      monitoringEnabled: false,
      monitoringGain: 1,
      inputDeviceId: null,
      ...options,
    };
  }

  public async startRecording(): Promise<void> {
    try {
      this.pcmChunks = [];
      this.mediaRecorderChunks = [];
      this.mediaRecorderMimeType = null;

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: this.options.echoCancellation || false,
          noiseSuppression: this.options.noiseSuppression || false,
          autoGainControl: this.options.autoGainControl || false,
          channelCount: this.options.channels || 1,
          ...(this.options.inputDeviceId ? { deviceId: { exact: this.options.inputDeviceId } } : {}),
        } as any,
      });

      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.setupAnalyser();

      try {
        await this.setupAudioWorklet();
      } catch (e) {
        console.warn("AudioWorklet unavailable, falling back to ScriptProcessor/MediaRecorder", e);
        try {
          this.fallbackToScriptProcessor();
        } catch (e2) {
          console.warn("ScriptProcessor unavailable, falling back to MediaRecorder", e2);
          this.fallbackToMediaRecorder();
        }
      }

      this.isRecording = true;
      this.recordingStartTime = Date.now();
    } catch (error) {
      console.error("Failed to start recording:", error);
      throw error;
    }
  }

  public async stopRecording(): Promise<DraftRecording> {
    if (!this.isRecording) {
      throw new Error("Not recording");
    }

    this.isRecording = false;
    const durationMs = Date.now() - this.recordingStartTime;

    if (this.sourceNode) {
      this.sourceNode.disconnect();
    }
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
      this.analyserData = null;
    }
    if (this.processorNode) {
      this.processorNode.disconnect();
    }
    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect();
      this.scriptProcessorNode.onaudioprocess = null;
      this.scriptProcessorNode = null;
    }
    if (this.scriptProcessorSilence) {
      this.scriptProcessorSilence.disconnect();
      this.scriptProcessorSilence = null;
    }
    if (this.monitorGainNode) {
      this.monitorGainNode.disconnect();
      this.monitorGainNode = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        const recorder = this.mediaRecorder!;
        const handleStop = () => {
          recorder.removeEventListener("stop", handleStop);
          resolve();
        };
        recorder.addEventListener("stop", handleStop, { once: true });
        recorder.stop();
      });
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }

    if (this.pcmChunks.length > 0) {
      const pcmFloat32 = this.concatenateFloat32Arrays(this.pcmChunks);
      const outSampleRate = this.options.sampleRate || 48000;
      const outChannels = 1; // we record mono (first channel)
      const resampled = WAVEncoder.resample(
        pcmFloat32,
        this.audioContext!.sampleRate,
        outSampleRate
      );

      const encoder = new WAVEncoder({
        sampleRate: outSampleRate,
        channels: outChannels,
        bitDepth: 16,
      });

      const blob = encoder.encode(resampled);
      const peaksData = PeaksComputer.computeFromFloat32(
        resampled,
        outSampleRate
      );

      return {
        blob,
        format: "wav",
        sampleRate: outSampleRate,
        channels: outChannels,
        durationMs,
        peaksJson: PeaksComputer.toJSON(peaksData),
      };
    }

    if (this.mediaRecorderChunks.length > 0) {
      const recordedBlob = new Blob(this.mediaRecorderChunks, {
        type: this.mediaRecorderMimeType || this.mediaRecorderChunks[0]?.type || "",
      });

      // Convert the MediaRecorder output to WAV for maximum playback compatibility (iOS can't play WebM/Opus).
      const arrayBuffer = await recordedBlob.arrayBuffer();
      let audioBuffer: AudioBuffer;
      try {
        audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(`Failed to decode MediaRecorder audio for WAV conversion: ${msg}`);
      }

      const outSampleRate = this.options.sampleRate || 48000;
      const outChannels = 1;
      const monoPcm = this.mixAudioBufferToMono(audioBuffer);
      const resampled = WAVEncoder.resample(monoPcm, audioBuffer.sampleRate, outSampleRate);

      const encoder = new WAVEncoder({
        sampleRate: outSampleRate,
        channels: outChannels,
        bitDepth: 16,
      });
      const wavBlob = encoder.encode(resampled);
      const peaksData = PeaksComputer.computeFromFloat32(resampled, outSampleRate);

      return {
        blob: wavBlob,
        format: "wav",
        sampleRate: outSampleRate,
        channels: outChannels,
        durationMs,
        peaksJson: PeaksComputer.toJSON(peaksData),
      };
    }

    throw new Error("No recording data captured");
  }

  public getInputLevel(): number {
    if (!this.analyserNode || !this.analyserData) return 0;
    this.analyserNode.getByteTimeDomainData(this.analyserData);

    let sumSq = 0;
    for (let i = 0; i < this.analyserData.length; i += 1) {
      const v = (this.analyserData[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this.analyserData.length);

    const gated = Math.max(0, rms - 0.02);
    const scaled = gated / 0.25;
    if (scaled <= 0) return 0;
    if (scaled >= 1) return 1;
    return scaled;
  }

  private setupAnalyser(): void {
    if (!this.audioContext || !this.sourceNode) return;
    if (this.analyserNode) return;

    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.72;

    this.analyserNode = analyser;
    this.analyserData = new Uint8Array(new ArrayBuffer(analyser.fftSize));

    // Connect in parallel with the recorder/monitoring graph.
    this.sourceNode.connect(analyser);
  }

  private async setupAudioWorklet(): Promise<void> {
    if (!this.sourceNode || !this.audioContext) {
      throw new Error("Audio context or source not initialized");
    }

    const processorScript = `
      class PCMRecorderProcessor extends AudioWorkletProcessor {
        process(inputs, outputs) {
          const input = inputs[0];
          if (input.length > 0) {
            this.port.postMessage({ pcmData: input[0] });
          }
          return true;
        }
      }
      registerProcessor('pcm-recorder', PCMRecorderProcessor);
    `;

    const blob = new Blob([processorScript], { type: "application/javascript" });
    const workletUrl = URL.createObjectURL(blob);

    try {
      await this.audioContext.audioWorklet.addModule(workletUrl);
    } finally {
      URL.revokeObjectURL(workletUrl);
    }

    this.processorNode = new AudioWorkletNode(
      this.audioContext,
      "pcm-recorder"
    );

    this.processorNode.port.onmessage = (event) => {
      if (event.data.pcmData) {
        this.pcmChunks.push(new Float32Array(event.data.pcmData));
      }
    };

    this.sourceNode.connect(this.processorNode);
    this.connectMonitoring();
  }

  private fallbackToScriptProcessor(): void {
    if (!this.sourceNode || !this.audioContext) {
      throw new Error("Audio context or source not initialized");
    }

    const bufferSize = 4096;
    const node = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

    node.onaudioprocess = (event) => {
      const input = event.inputBuffer;
      if (input.numberOfChannels <= 0) return;

      if (input.numberOfChannels === 1) {
        this.pcmChunks.push(new Float32Array(input.getChannelData(0)));
        return;
      }

      const frames = input.length;
      const channelData: Float32Array[] = [];
      for (let c = 0; c < input.numberOfChannels; c += 1) {
        channelData.push(input.getChannelData(c));
      }

      const mixed = new Float32Array(frames);
      for (let i = 0; i < frames; i += 1) {
        let sum = 0;
        for (let c = 0; c < channelData.length; c += 1) {
          sum += channelData[c][i];
        }
        mixed[i] = sum / channelData.length;
      }
      this.pcmChunks.push(mixed);
    };

    this.sourceNode.connect(node);

    // ScriptProcessor needs to be connected to an output to keep processing.
    this.scriptProcessorSilence = this.audioContext.createGain();
    this.scriptProcessorSilence.gain.value = 0;
    node.connect(this.scriptProcessorSilence);
    this.scriptProcessorSilence.connect(this.audioContext.destination);

    this.scriptProcessorNode = node;
    this.connectMonitoring();
  }

  private fallbackToMediaRecorder(): void {
    if (!this.mediaStream) {
      throw new Error("No media stream available");
    }

    const candidates = [
      "audio/webm;codecs=opus",
      'audio/webm; codecs="opus"',
      "audio/webm",
      "audio/mp4",
      'audio/mp4;codecs="mp4a.40.2"',
    ];
    const mimeType = candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
    if (!mimeType) {
      throw new Error("No supported MediaRecorder audio MIME type found");
    }
    this.mediaRecorderMimeType = mimeType;

    this.mediaRecorder = new MediaRecorder(this.mediaStream, {
      mimeType,
      audioBitsPerSecond: 192000,
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.mediaRecorderChunks.push(event.data);
      }
    };

    this.mediaRecorder.start();
    this.connectMonitoring();
  }

  private mixAudioBufferToMono(audioBuffer: AudioBuffer): Float32Array {
    const channels = Math.max(1, audioBuffer.numberOfChannels);
    const frames = audioBuffer.length;
    if (channels === 1) {
      return new Float32Array(audioBuffer.getChannelData(0));
    }

    const channelData: Float32Array[] = [];
    for (let c = 0; c < channels; c += 1) {
      channelData.push(audioBuffer.getChannelData(c));
    }

    const mixed = new Float32Array(frames);
    for (let i = 0; i < frames; i += 1) {
      let sum = 0;
      for (let c = 0; c < channelData.length; c += 1) {
        sum += channelData[c][i];
      }
      mixed[i] = sum / channelData.length;
    }
    return mixed;
  }

  private connectMonitoring(): void {
    if (!this.sourceNode || !this.audioContext || !this.options.monitoringEnabled) {
      return;
    }

    if (this.monitorGainNode) {
      this.monitorGainNode.disconnect();
    }

    this.monitorGainNode = this.audioContext.createGain();
    this.monitorGainNode.gain.value = this.options.monitoringGain ?? 1;
    this.sourceNode.connect(this.monitorGainNode);
    this.monitorGainNode.connect(this.audioContext.destination);
  }

  private concatenateFloat32Arrays(arrays: Float32Array[]): Float32Array {
    const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Float32Array(totalLength);
    let offset = 0;

    for (const array of arrays) {
      result.set(array, offset);
      offset += array.length;
    }

    return result;
  }
}
